import { useEffect, useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import type { Match } from '@/lib/matches';
import { formatMatchTime, isMatchLive, isMatchUpcoming, timeUntil } from '@/lib/matches';
import { PageHeader } from '@/components/common/PageHeader';

function MatchCard({ match }: { match: Match }) {
  const live = isMatchLive(match);
  const upcoming = isMatchUpcoming(match) && !live;
  const score = match.score.fullTime;
  const hasScore = score.home !== null || score.away !== null;

  return (
    <div className="flex-shrink-0 w-[220px] bg-bg-elevated rounded-lg border border-border p-3 hover:border-accent/40 transition-colors flex flex-col gap-2">
      <div className="flex items-center gap-1.5 text-[11px] text-text-muted">
        {match.competition.emblem && (
          <img src={match.competition.emblem} alt="" className="w-3.5 h-3.5 object-contain" />
        )}
        <span className="truncate">{match.competition.name}</span>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0 text-right">
          <span className="text-sm font-medium truncate block">{match.homeTeam.shortName || match.homeTeam.name}</span>
        </div>
        <div className="flex-shrink-0 text-center min-w-[32px]">
          {live && hasScore ? (
            <span className="text-sm font-bold tabular-nums text-accent">{score.home}-{score.away}</span>
          ) : upcoming && match.status === 'TIMED' ? (
            <span className="text-xs tabular-nums text-text-muted">{timeUntil(match.utcDate)}</span>
          ) : (
            <span className="text-xs text-text-muted">vs</span>
          )}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <span className="text-sm font-medium truncate block">{match.awayTeam.shortName || match.awayTeam.name}</span>
        </div>
      </div>

      <div className="flex items-center justify-between text-[11px] text-text-muted">
        <span>{formatMatchTime(match.utcDate)}</span>
        {live && <span className="text-accent font-semibold">LIVE</span>}
        {upcoming && match.status === 'SCHEDULED' && (
          <span className="text-text-muted">Scheduled</span>
        )}
      </div>
    </div>
  );
}

export function UpcomingMatches() {
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchMatches() {
      try {
        const res = await fetch('/data/worldcup-matches.json');
        if (!res.ok) throw new Error(`Failed to load matches`);
        const data = await res.json();
        if (!cancelled) {
          const all: Match[] = data.matches || [];
          const relevant = all.filter(
            (m) => isMatchLive(m) || (isMatchUpcoming(m) && m.status === 'TIMED')
          );
          relevant.sort((a, b) => new Date(a.utcDate).getTime() - new Date(b.utcDate).getTime());
          setMatches(relevant.slice(0, 30));
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
          setLoading(false);
        }
      }
    }

    fetchMatches();
    const interval = setInterval(fetchMatches, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const scroll = (dir: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const amount = 480;
    scrollRef.current.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <section>
        <PageHeader size="section" icon={<Calendar className="w-5 h-5 text-accent" />} title="Upcoming Matches" />
        <div className="flex gap-3 mt-3 overflow-hidden">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex-shrink-0 w-[220px] h-[88px] bg-bg-hover rounded-lg animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section>
        <PageHeader size="section" icon={<Calendar className="w-5 h-5 text-accent" />} title="Upcoming Matches" />
        <p className="text-xs text-text-muted mt-2">Unable to load match schedule</p>
      </section>
    );
  }

  if (matches.length === 0) {
    return null;
  }

  return (
    <section>
      <PageHeader
        size="section"
        icon={<Calendar className="w-5 h-5 text-accent" />}
        title="Upcoming Matches"
        subtitle={`Next ${matches.length} matches`}
      />
      <div className="relative mt-3 group">
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-bg/80 backdrop-blur-sm border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-bg-hover"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent"
          style={{ scrollbarWidth: 'thin' }}
        >
          {matches.map((m) => (
            <MatchCard key={m.id} match={m} />
          ))}
        </div>
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-bg/80 backdrop-blur-sm border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-bg-hover"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}
